import { Button, Col, Row, Space, Typography } from 'antd';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaymentMethodId, usePlanId, usePaymentCreate } from '../../../../../hooks/stripe';
import ContentCard from '../../../../Common/ContentCard';
import Loading from '../../../../Common/Loading';
import PaymentItem from '../../../../Common/PaymentItem';
import styles from '../../index.module.less';
import { formatPrice } from '../SelectPlan/PlanRow';
import { setDataInStorage } from '../../../../../utils/storage';
import { useAuth } from '../../../../../context/auth';

interface ISelectPlan {
  promoCode: string | undefined;
  selectedPlan: string | null;
  selectedPaymentMethod: string | null;
  handleBack: () => void;
  discount: any;
}

const Confirm = ({
  selectedPlan,
  selectedPaymentMethod,
  handleBack,
  promoCode,
  discount,
}: ISelectPlan): JSX.Element => {
  const navigate = useNavigate();
  const planId = usePlanId();
  const paymentMethodId = usePaymentMethodId();
  const paymentCreate = usePaymentCreate();
  const { setSubscriptionData } = useAuth()

  useEffect(() => {
    if (!selectedPaymentMethod) return;
    paymentMethodId.fetch(undefined, selectedPaymentMethod);
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (!selectedPlan) return;
    planId.fetch(undefined, selectedPlan);
  }, [selectedPlan]);

  const handleConfirm = () => {
    if (!selectedPlan || !selectedPaymentMethod) return;

    paymentCreate
      .fetch({
        pmId: selectedPaymentMethod,
        planId: selectedPlan,
        promoCode,
        isFreeTrial: !!planId?.data?.data.trial_period_days,
      })
      .then((res) => {
        const subscriptionId = res?.data?.id;

        setDataInStorage('signUpData', {});
        const purchaseValue = formatPrice((plan?.amount ?? 0) * ((100 - discount) / 100)) || 0;

        // Facebook Pixel
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            value: purchaseValue,
            currency: 'USD',
            discount,
          });
        }

        // Google Tag Manager
        if (!window.dataLayer) {
          window.dataLayer = [];
        }
        window.dataLayer.push({
          event: 'Purchase',
          value: purchaseValue,
          currency: 'USD',
          discount,
        });

        // Reddit Pixel
        if (window.rdt) {
          window.rdt('track', 'Purchase', {
            value: purchaseValue,
            currency: 'USD',
            discount,
          });
        }

        setSubscriptionData({ subscriptionId })

        navigate('/home', { replace: true, state: { isFirstLogin: true } })
      })
  };

  const plan = planId.data?.data;
  const displayInterval = plan?.interval === 'year' ? 'yr' : 'mo';

  return (
    <Space direction="vertical" size={20}>
      <ContentCard loading={planId.loading || paymentMethodId.loading}>
        <Loading visible={paymentCreate.loading} absolute />
        <Space direction="vertical" size={20}>
          {planId.data && (
            <Space direction="vertical" size={8}>
              <Typography.Title level={3}>Your Plan</Typography.Title>
              <div className={styles.row}>
                <Typography className="color-gray">
                  {plan?.nickname}
                </Typography>
                <div className="flex-row flex-align-center gap-12">
                  {!!promoCode && plan?.amount && (
                    <Typography.Title level={3} className={styles.price}>
                      {formatPrice(plan.amount * ((100 - discount) / 100))}/{displayInterval}
                    </Typography.Title>
                  )}

                  <Typography.Title
                    level={3}
                    className={clsx(styles.price, {
                      'line-through color-gray': promoCode,
                    })}
                  >
                    {plan && formatPrice(plan.amount || 0)}/{displayInterval}
                  </Typography.Title>
                </div>
              </div>
            </Space>
          )}
          {!!paymentMethodId.data?.data && (
            <Space direction="vertical" size={8}>
              <Typography.Title level={3}>Payment method</Typography.Title>
              <PaymentItem key={paymentMethodId.data?.data.id} card={paymentMethodId.data?.data?.card} />
            </Space>
          )}
        </Space>
      </ContentCard>
      <Row className='flex-justify-end' style={{ marginBottom: '2px' }}>
        <Col className='w-fit-content' style={{ height: '44px' }}>
          <Button size="large" type="default" block onClick={handleBack} disabled={paymentCreate.loading}>
            Back
          </Button>
        </Col>
        <Col style={{ marginLeft: '8px', marginRight: '2px', height: '44px' }}>
          <Button
            size="large"
            type="primary"
            block
            onClick={handleConfirm}
            loading={paymentCreate.loading}
            disabled={paymentCreate.loading}
          >
            Confirm
          </Button>
        </Col>
      </Row>
    </Space>
  );
};

Confirm.defaultProps = {
  coupon: undefined,
};

export default Confirm;
